import React from 'react';
import _ from 'lodash';

import {
  errorUtil,
  fields,
  validationUtil
} from 'iron-forge-react-modules';
import {
  Controller,
  useFieldArray,
  useForm
} from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import {
  appFormUtil,
  FormModal,
  NetSuiteRecordChip,
  routeDirectory,
  selectFields
} from 'modules';

import { DeleteForever } from '@mui/icons-material';
import Big from 'big.js';
import * as uuid from 'uuid';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const {
  genController
} = selectFields.ReasonForReturnSelectField.genComponentAndControllerGenerator();
const ControlledReasonForReturnSelectField = genController<types.IFormState>();



export interface IProps {
  orderId: string;
  asCustomer?: boolean;

  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId, props.asCustomer ?? false);
  const form = useForm<types.IFormState>({
    defaultValues: {
      initialNote: '',
      lineItems: []
    }
  });
  const lineItems = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'lineItems'
  });

  const [
    initiateReturnAuthorization,
    initiateReturnAuthorizationTuple
  ] = dataService.useInitiateReturnAuthorization(props.orderId);
  const [
    initiateReturnAuthorizationAsCustomer,
    initiateReturnAuthorizationAsCustomerTuple
  ] = dataService.useInitiateReturnAuthorizationAsCustomer(props.orderId);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      await errorUtil.errorWrap(async () => {
        const trimmedFormState = appFormUtil.trimFormStateValues(formState);
        __validateDataFormat(trimmedFormState);
        __validateQuantities(trimmedFormState, queryTuple.data.lineItems);
        __validateRefundAmounts(trimmedFormState, queryTuple.data.lineItems);
        __validateSerialNumbers(trimmedFormState);
        if (queryTuple.data.isSmartPayOrder) {
          __validatePartialReturn(trimmedFormState, queryTuple.data.lineItems);
        }

        const confirmResponse = window.confirm("Are you sure you want to initiate this return?");
        if (!confirmResponse) {
          return;
        }

        if (props.asCustomer) {
          await initiateReturnAuthorizationAsCustomer(formState);
        } else {
          await initiateReturnAuthorization(formState);
        }
        props.onClose();
      }, {
        NO_LINE_ITEMS: "Please select at least one line item",
        INVALID_FORMAT: "Data format is invalid.",
        INVALID_QUANTITY: "Cannot return more than what was ordered.",
        INVALID_REFUND_AMOUNT: "Refund amount is higher than what was paid.",
        CANNOT_REFUND_GREATER_THAN_PURCHASED_AMOUNTS: "There was an error creating the return.",
        IS_PARTIAL_RETURN: "Partial returns on orders paid with SmartPay are not supported.",
        DUPLICATE_SERIAL_NUMBERS: "An IMEI/ICCID is selected more than once."
      });
    },
    [
      initiateReturnAuthorization,
      initiateReturnAuthorizationAsCustomer,
      props,
      queryTuple.data.isSmartPayOrder,
      queryTuple.data.lineItems
    ]
  );



  const addLineItem = React.useCallback(
    (orderLineItem: dataService.ILineItem, formLineItems: types.ILineItem[]) => {
      lineItems.append({
        id: uuid.v4(),
        lineItemId: orderLineItem.id,
        quantity: '1',
        serialNumber: '',
        refundAmountDollars: __getDefaultRefundAmount(orderLineItem, formLineItems)
      });
    },
    [lineItems]
  );



  const order = queryTuple.data.order;
  const loading = initiateReturnAuthorizationTuple.loading || initiateReturnAuthorizationAsCustomerTuple.loading;

  return (
    <FormModal.FC
      title="Select Items to Return"
      onClose={props.onClose}
      isLoading={queryTuple.loading}
      onSubmit={form.handleSubmit(processSubmit)}
      isDoubleWide
      fnGenElAcceptButton={(args => (
        <Button
          {...args}
          variant="contained"
          type="submit">
          Initiate Return
        </Button>
      ))}>
      {!props.asCustomer && (
        <Grid
          container
          spacing={2}>
          <Grid
            item
            xs={2}>
            <Typography>Customer Name</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <Link
              href={routeDirectory.admin.customers.path(order.customerId ?? '')}
              target="_blank">
              {order.customerFullName ?? ''}
            </Link>
          </Grid>

          <Grid
            item
            xs={2}>
            <Typography>NS Customer</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <NetSuiteRecordChip.FC writeStatus={order.customerWriteStatus} />
          </Grid>

          <Grid
            item
            xs={2}>
            <Typography>Order Number</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <Link
              href={routeDirectory.partner.storeDetail.path(order.storeId ?? '', props.orderId)}
              target="_blank">
              {order.orderNumber}
            </Link>
          </Grid>

          <Grid
            item
            xs={2}>
            <Typography>NS SO</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <NetSuiteRecordChip.FC writeStatus={order.writeStatus} />
          </Grid>

          <Grid
            item
            xs={2}>
            <Typography>Order Date</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <Typography>{order.orderDate.toFormat('MM/d/yyyy')}</Typography>
          </Grid>

          <Grid
            item
            xs={2}>
            <Typography>NS Fulfillment</Typography>
          </Grid>
          <Grid
            item
            xs={4}>
            <NetSuiteRecordChip.FC writeStatus={order.itemFulfillmentWriteStatus} />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        spacing={2}>
        {_.map(queryTuple.data.lineItems, orderLineItem => (
          <Grid
            key={orderLineItem.id}
            item
            xs={12}>
            <Accordion
              elevation={0}
              defaultExpanded
              expanded>
              <AccordionSummary>
                <Grid
                  container
                  spacing={1}>
                  <Grid
                    item
                    xs={4}>
                    <Typography variant="caption">
                      Product Name
                    </Typography>
                    <Typography>
                      {orderLineItem.productName}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={2}>
                    <Typography variant="caption">
                      Remaining Quantity
                    </Typography>
                    <Typography>
                      {__getRemainingQuantity(orderLineItem, lineItems.fields)}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={2}>
                    <Typography variant="caption">
                      Remaining Refund
                    </Typography>
                    <Typography>
                      ${__getRemainingRefundAmount(orderLineItem, lineItems.fields).toFixed(2)}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Button
                      disabled={__getRemainingQuantity(orderLineItem, lineItems.fields) === 0}
                      onClick={() => addLineItem(orderLineItem, lineItems.fields)}>
                      Add Line Item
                    </Button>
                  </Grid>
                </Grid>
              </AccordionSummary>

              <AccordionDetails>
                {__getFilteredFormLineItems(orderLineItem, lineItems.fields)
                  .map(formLineItem => {
                    const rootIndex = __getRootIndex(lineItems.fields, formLineItem);
                    return (
                      <Grid
                        container
                        key={rootIndex}
                        spacing={1}>
                        <Grid
                          item
                          xs={4}>
                          {orderLineItem.type === 'serialized' && (
                            <Controller
                              name={`lineItems.${rootIndex}.serialNumber`}
                              control={form.control}
                              render={({ field }) => (
                                <Select
                                  disabled={loading}
                                  size="small"
                                  fullWidth
                                  required={orderLineItem.type === 'serialized'}
                                  value={field.value}
                                  onChange={field.onChange}>
                                  {_.map(orderLineItem.availableSerialNumbers, sn => (
                                    <MenuItem
                                      value={sn}
                                      key={sn}>
                                      {sn}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )} />
                          )}
                        </Grid>

                        <Grid
                          item
                          xs={2}>
                          <ControlledTextField
                            isDisabled={loading || orderLineItem.type === 'serialized'}
                            label="Quantity"
                            formControl={form.control}
                            isRequired
                            fieldKey={`lineItems.${rootIndex}.quantity`}
                            size="small"
                            // This is to get around the typechecking of the Iron Forge API modules field
                            extraValidationRules={{
                              max: orderLineItem.quantity,
                              min: 1
                            }} />
                        </Grid>

                        {!props.asCustomer && (
                          <Grid
                            item
                            xs={2}>
                            <ControlledTextField
                              isDisabled
                              label="Refund Amount ($)"
                              formControl={form.control}
                              isRequired
                              size="small"
                              fieldKey={`lineItems.${rootIndex}.refundAmountDollars`}
                              extraValidationRules={{
                                max: _.toNumber(formLineItem.refundAmountDollars),
                                min: 0
                              }} />
                          </Grid>
                        )}

                        <Grid item xs={3}>
                          <ControlledReasonForReturnSelectField
                            extraValidationRules={{ required: true }}
                            formControl={form.control}
                            fieldKey={`lineItems.${rootIndex}.reasonForReturn`}
                            isDisabled={loading}
                            isRequired />
                        </Grid>

                        <Grid item xs={1}>
                          <IconButton
                            disabled={loading}
                            onClick={() => {
                              const indexOfLineItem = _.indexOf(lineItems.fields, formLineItem);
                              lineItems.remove(indexOfLineItem);
                            }}>
                            <DeleteForever />
                          </IconButton>
                        </Grid>
                      </Grid>
                    );
                  })}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}

        <Grid
          item
          xs={12}>
          <ControlledTextField
            isDisabled={loading}
            fieldKey="initialNote"
            formControl={form.control}
            label="Note"
            numLines={5} />
        </Grid>
      </Grid>
    </FormModal.FC>
  );
};



function __getRemainingQuantity(orderLineItem: dataService.ILineItem, formLineItems: types.ILineItem[]) {
  const relevantLineItems = __getFilteredFormLineItems(orderLineItem, formLineItems);
  return orderLineItem.quantity - _.sumBy(relevantLineItems, li => _.toNumber(li.quantity));
}



function __getFilteredFormLineItems(orderLineItem: dataService.ILineItem, formLineItems: types.ILineItem[]) {
  const lineItems = formLineItems.filter(formLineItem => formLineItem.lineItemId === orderLineItem.id);
  return lineItems;
}



function __getRemainingRefundAmount(orderLineItem: dataService.ILineItem, formLineItems: types.ILineItem[]) {
  const relevantLineItems = __getFilteredFormLineItems(orderLineItem, formLineItems);
  return _.reduce(
    relevantLineItems,
    (remainingAmount, lineItem) =>
      remainingAmount.sub(new Big(lineItem.refundAmountDollars)),
    orderLineItem.lineTotalDollars
  );
}



function __getDefaultRefundAmount(orderLineItem: dataService.ILineItem, formLineItems: types.ILineItem[]) {
  const remainingQuantity = __getRemainingQuantity(orderLineItem, formLineItems);
  const remainingRefundAmount = __getRemainingRefundAmount(orderLineItem, formLineItems);
  return remainingRefundAmount.div(remainingQuantity).toFixed(2, 1);
}



function __getRootIndex(formLineItems: types.ILineItem[], lineItem: types.ILineItem) {
  return _.findIndex(formLineItems, li => li.id === lineItem.id);
}



function __validateDataFormat(formState: types.IFormState) {
  if (_.isEmpty(formState.lineItems)) {
    throw new Error('NO_LINE_ITEMS');
  }
  const inputsAreNumbers = _.every(formState.lineItems, lineItem =>
    validationUtil.reDecimalNumber.test(lineItem.quantity) &&
    _.toNumber(lineItem.quantity) % 1 === 0 && // Must be a whole number
    validationUtil.reDecimalNumber.test(lineItem.refundAmountDollars)
  );
  if (!inputsAreNumbers) {
    throw new Error('INVALID_FORMAT');
  }
}



function __validateQuantities(formState: types.IFormState, orderLineItems: dataService.ILineItem[]) {
  const isValid = _.every(orderLineItems, lineItem => {
    // Validate quantities
    const relevantLineItems = __getFilteredFormLineItems(lineItem, formState.lineItems);
    return _.isEmpty(relevantLineItems) ||
      _.sumBy(relevantLineItems, li => _.toNumber(li.quantity)) <= lineItem.quantity;
  });

  if (!isValid) {
    throw new Error('INVALID_QUANTITY');
  }
}



/**
 * This function will validate that the sum of the refunds from the form is less than the original amount purchased.
 *
 * @param formState
 * @param orderLineItems
 */
function __validateRefundAmounts(formState: types.IFormState, orderLineItems: dataService.ILineItem[]) {
  const isValid = _.every(orderLineItems, lineItem => {
    const relevantLineItems = __getFilteredFormLineItems(lineItem, formState.lineItems);
    const sumOfFormLineItems = _.reduce(
      relevantLineItems,
      (sum, lineItem) => sum.add(new Big(lineItem.refundAmountDollars)),
      new Big(0)
    );

    return _.isEmpty(relevantLineItems) || sumOfFormLineItems.lte(lineItem.lineTotalDollars);
  });

  if (!isValid) {
    throw new Error('INVALID_REFUND_AMOUNT');
  }
}



function __validatePartialReturn(formState: types.IFormState, orderLineItems: dataService.ILineItem[]) {
  const isPartialReturn = _.some(orderLineItems, lineItem => {
    const relevantLineItems = __getFilteredFormLineItems(lineItem, formState.lineItems);
    return _.isEmpty(relevantLineItems) ||
      _.sumBy(relevantLineItems, li => _.toNumber(li.quantity)) < lineItem.quantity;
  });

  if (isPartialReturn) {
    throw new Error('IS_PARTIAL_RETURN');
  }
}



function __validateSerialNumbers(formState: types.IFormState) {
  const allSerialNumbers = _.compact(_.map(formState.lineItems, li => li.serialNumber));
  const uniques = _.uniq(allSerialNumbers);
  if (uniques.length !== allSerialNumbers.length) {
    throw new Error('DUPLICATE_SERIAL_NUMBERS');
  }
}
